import { useState, useEffect, useMemo, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import axios from "axios";
import config from '../../config';
import dayjs from 'dayjs';

import '../../styles/global.css';
import '../../styles/header.css';
import '../../styles/tables.css';
import '../../styles/buttons.css';
import '../../styles/popup.css';
import '../../styles/status.css';

import PageHeader from "../../components/general/pageHeader";
import MoneyCard from "../../components/cards/moneyCard";
import BalanceTransactionCards from "../../components/cards/mBalanceTransactionsCards";
import ChargeInvoiceForm from "../../components/forms/chargeInvoiceForm";
import LoadBalanceForm from "../../components/forms/loadBalanceForm";
import GeneratedSOA from "../../components/portfolio/generatedSOA"
import Loading from "../../components/general/loading";

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Tooltip } from '@mui/material';

const MBalanceTransactions = () => {
    const location = useLocation();
    const queryClient = useQueryClient();

    const { data: properties, isLoading: isPropertiesLoading } = useQuery({
        queryKey: ['v2-properties'],
        queryFn: fetchProperties,
    });

    const { data: rolesData, isLoading: isRolesLoading } = useQuery({
        queryKey: ['v2-roles'],
        queryFn: fetchRoles,
    });

    const { data: creditBalances, isLoading: isCreditBalancesLoading } = useQuery({
        queryKey: ['v2-creditBalances'],
        queryFn: fetchBalances,
    });

    const { data: transactions, isLoading: isTransactionsLoading } = useQuery({
        queryKey: ['v2-transactions'],
        queryFn: fetchTransactions,
    });

    const { data: invoices, isLoading: isInvoicesLoading } = useQuery({
        queryKey: ['v2-invoices'],
        queryFn: fetchInvoices,
    });

    const [paymentAllocations, setPaymentAllocations] = useState({});

    const [selectedAccount, setSelectedAccount] = useState(null);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [selectedUnits, setSelectedUnits] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);

    const [startDate, setStartDate] = useState(dayjs());
    const [endDate, setEndDate] = useState(dayjs());

    const [invoiceOverlayOpen, setInvoiceOverlayOpen] = useState(false);
    const [paymentOverlayOpen, setPaymentOverlayOpen] = useState(false);
    const [soaOverlayOpen, setSOAOverlayOpen] = useState(false);
    const [editAllocOverlayOpen, setEditAllocOverlayOpen] = useState(false);
    const [viewAllocOverlayOpen, setViewAllocOverlayOpen] = useState(false);

    const [activeTab, setActiveTab] = useState('invoices');

    // Get all accounts from roles
    const accountOptions = useMemo(() => {
        if (!rolesData) return [];
        const map = {};
        rolesData?.forEach(r => {
            const acc = r.propertyRole.account;
            if (!map[acc._id]) {
                map[acc._id] = {
                    label: `${acc.user.givenName} ${acc.user.lastName}`,
                    value: acc._id
                };
            }
        });
        return Object.values(map);
    }, [rolesData]);

    useEffect(() => {
        console.log("Account options:", accountOptions);
    }, [accountOptions]);

    // Filter properties owned by the selected account
    const propertyOptions = useMemo(() => {
        if (!rolesData || !selectedAccount) return [];
        const relevant = rolesData?.filter(r => r.propertyRole.account._id === selectedAccount.value);
        const props = new Set();
        relevant?.forEach(r => props.add(r.propertyRole.property));
        return properties?.filter(p => props.has(p.value));
    }, [rolesData, selectedAccount, properties]);

    // Filter units owned by the selected account and selected property
    const unitOptions = useMemo(() => {
        if (!selectedAccount || !selectedProperty) return [];
        const relevant = rolesData?.filter(r =>
            r.propertyRole.account._id === selectedAccount.value &&
            r.propertyRole.property === selectedProperty.value
        );
        const map = {};
        relevant?.forEach(r => {
            map[r.unit.unitNumber] = {
                label: r.unit.unitNumber,
                value: r.unit.unitNumber
            };
        });
        return Object.values(map);
    }, [rolesData, selectedAccount, selectedProperty]);

    // If fetched from portfolio...
    useEffect(() => {
        if (location?.state) {
            setSelectedAccount({
                label: location.state.accountName,
                value: location.state.accountId
            });
            setSelectedProperty({ label: location.state.property, value: location.state.propertyId });
            setSelectedUnits(location.state.unit.map(u => ({ label: u, value: u })));
        }
    }, [location?.state]);

    // Else set to first account...
    useEffect(() => {
        // ✅ Skip entirely if coming from portfolio
        if (location?.state) return;

        if (accountOptions.length === 0) return;

        // If no account selected yet, set the first one
        if (!selectedAccount) {
            setSelectedAccount(accountOptions[0]);
            return;
        }

        // If selected account no longer exists in the options, reset to first
        const matched = accountOptions.find(acc => acc.value === selectedAccount.value);
        if (!matched) {
            setSelectedAccount(accountOptions[0]);
        }
    }, [accountOptions, selectedAccount, location?.state]);

    // and the first property once account is selected
    useEffect(() => {
        // ✅ Skip if coming from portfolio
        if (location?.state) return;

        if (!isPropertiesLoading && selectedAccount && !selectedProperty) {
            setSelectedProperty(propertyOptions?.[0] || null);
        }
    }, [isPropertiesLoading, propertyOptions, selectedAccount, selectedProperty, location?.state]);

    // Combine payment transactions and invoices
    const { combinedRows, accountInvoices } = useMemo(() => {
        if (!selectedAccount) return { combinedRows: [], accountInvoices: [] };

        // Filter only data related to the selected account
        const accountTransactions = transactions?.filter(
            txn => txn.account?._id === selectedAccount.value
        ) || [];

        const accountInvoices = invoices?.filter(
            inv => inv.chargedTo?.propertyRole?.account === selectedAccount.value
        ) || [];

        // Format transactions (payments)
        const formattedTransactions = accountTransactions.map(txn => {
            const paymentDetails = txn.transactionRecord?.payment_details;

            return {
                id: txn._id,
                type: "Payment",
                property: txn.accountBalance?.propertyRole?.property?.name || null,
                reference: txn.transactionReference || "-",
                cd: txn.type === "credit" ? "Credit" : "Debit",
                issuedBy: null,
                vendor: null,
                isRecurring: null,
                status: null,
                createdAt: txn.createdAt,
                updatedAt: txn.updatedAt,
                dueDate: null,
                amount: txn.amount,
                invoiceItems: [],
                paymentDate: txn.createdAt,
                paymentMethod: paymentDetails?.type
                    ? paymentDetails.type.charAt(0).toUpperCase() + paymentDetails.type.slice(1)
                    : null,
                cardNumber: paymentDetails?.last4 || null,
                allocatedAmount: null,
                openBalance: null,
                isInvoice: false,
            };
        });

        // Format invoices
        const formattedInvoices = accountInvoices.map(inv => {
            const allocatedAmount = inv.totalAmount - inv.balance;

            return {
                id: inv._id,
                type: "Invoice",
                property: inv.property.name || null,
                unit: inv.invoiceItems?.[0]?.unit?.unitNumber || null,
                reference: inv.status?.toLowerCase() === "pending" ? "-" : inv._id,
                cd: "Debit",
                issuedBy: inv.issuedBy?.user?.givenName + " " + inv.issuedBy?.user?.lastName || null,
                vendor: inv.vendor || null,
                isRecurring: inv.frequency || null,
                status: inv.status || null,
                createdAt: inv.createdAt,
                updatedAt: inv.updatedAt,
                dueDate: inv.dueDate,
                amount: inv.totalAmount,
                invoiceItems: inv.invoiceItems || [],
                paymentDate: inv.updatedAt,
                paymentMethod: null,
                cardNumber: null,
                allocatedAmount,
                openBalance: inv.balance,
                isInvoice: true,
            };
        });

        const combinedRows = [...formattedTransactions, ...formattedInvoices].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        return { combinedRows, accountInvoices };
    }, [transactions, invoices, selectedAccount]);

    // Calculating the balance, invoice amount, and past due amount
    const balanceEntry = creditBalances?.find(b => b.propertyRole?.account === selectedAccount?.value);
    const now = new Date();
    const invoiceAmount = accountInvoices
        ?.filter(t => t.status !== "Paid")
        ?.reduce((sum, t) => sum + (t.balance ?? 0), 0);
    const pastDue = accountInvoices
        ?.filter(t => t.status !== "Paid" && new Date(t.dueDate) < now)
        ?.reduce((sum, t) => sum + (t.balance ?? 0), 0);
    const balance = balanceEntry?.credit || 0;

    // Filtering combinedRows based on property and unit selection
    const filteredRows = useMemo(() => {
        if (!selectedProperty) {
            return null; // means "no property selected"
        }

        // Start from combined rows
        let rows = [...combinedRows];

        // Filter by property
        rows = rows.filter(r => r.property === selectedProperty.label);

        // Filter invoices by unit if selected
        if (selectedUnits?.length > 0) {
            const selectedUnitNumbers = selectedUnits.map(u => u.value);
            rows = rows.filter(r =>
                r.isInvoice ? selectedUnitNumbers.includes(r.unit) : true
            );
        }

        return rows;
    }, [combinedRows, selectedProperty, selectedUnits]);

    // View invoice or payment details
    const handleRowClick = (item) => {
        console.log("Selected item:", item);
        setSelectedRow(item);
        if (item.isInvoice) {
            setInvoiceOverlayOpen(true);
        }
        else {
            setPaymentOverlayOpen(true);
        }
    };

    // View allocation details
    const handleViewAllocation = (item) => {
        setSelectedRow(item);
        setViewAllocOverlayOpen(true);
    };

    // Edit allocation details
    const viewEditAllocation = (item) => {
        setSelectedRow(item);
        setEditAllocOverlayOpen(true);
    };

    // For allocating amount
    useEffect(() => {
        if (selectedRow?.invoiceItems) {
            const initial = {};
            selectedRow.invoiceItems?.forEach(item => {
                initial[item._id] = 0;
            });
            setPaymentAllocations(initial);
        }
    }, [selectedRow]);

    // Merge payment allocations by itemId, summing allocatedAmount for duplicates
    const handleAllocationChange = (e, itemId) => {
        const newAllocatedAmount = parseFloat(e.target.value) || 0;

        setPaymentAllocations(prev => ({
            ...prev,
            [itemId]: newAllocatedAmount
        }));

        console.log("Changed allocation for itemId:", itemId, "=>", newAllocatedAmount);
    };

    // Send edited allocation to backend
    const handleEditAllocation = async (e, item) => {
        e.preventDefault();

        try {
            const paymentData = {
                invoiceId: item.reference === "-" ? item.id : item.reference, // Use item.id if invoice has no transaction yet, use reference if it does
                account: selectedAccount.value,
                amount: item.amount,
                paymentMethod: 'Credit Card',
                paymentDate: new Date().toISOString(),
            };

            console.log("Invoice id:", item.reference);

            const allocations = Object.entries(paymentAllocations)?.map(([itemId, allocatedAmount]) => ({
                itemId,
                allocatedAmount,
            }));

            const data = {
                paymentData,
                allocations,
            };

            console.log("Payment data:", data);

            const response = await axios.post(`${config.API_URL}/v2/payment`, data, {
                headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
            });

            console.log("Allocation created:", response.data);

            queryClient.invalidateQueries(['v2-transactions']);
            queryClient.invalidateQueries(['v2-invoices']);

            setSelectedRow(null);
            setEditAllocOverlayOpen(false);
        } catch (error) {
            console.error("Error creating allocation:", error);
        }
    };

    // Printing the SOA
    const contentRef = useRef(null);
    const reactToPrintFn = useReactToPrint({
        contentRef,
        onAfterPrint: () => {
            // Reset dates and close the popup after printing
            setStartDate(dayjs());
            setEndDate(dayjs());
            setSOAOverlayOpen(false);
        },
    });

    const isAnyLoading =
        isRolesLoading ||
        isPropertiesLoading ||
        isCreditBalancesLoading ||
        isTransactionsLoading ||
        isInvoicesLoading;

    if (isAnyLoading) return <Loading />;

    return (
        <div className="w-full">
            <PageHeader pageName="Balance and Transactions" />
            <div className="flex flex-col md:flex-row md:gap-[15px] space-between mt-[20px] pb-[15px] border-b-[1.5px] border-[#E8E8E8] gap-0">
                <div className="account-dropdown md:mr-[15px]">
                    <div className="w-full md:w-[300px]">
                        <Autocomplete
                            options={accountOptions}
                            value={accountOptions.length === 0 ? null : selectedAccount}
                            loading={accountOptions.length === 0}
                            onChange={(_, value) => setSelectedAccount(value)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={accountOptions.length === 0 ? "Loading accounts..." : "Select Account"}
                                    variant="outlined"
                                    fullWidth
                                />
                            )}
                            sx={{ marginBottom: '15px' }}
                        />
                    </div>
                    <div className="w-full md:w-[300px]">
                        <Autocomplete
                            options={propertyOptions}
                            value={selectedProperty}
                            onChange={(e, val) => {
                                setSelectedProperty(val);
                                setSelectedUnits([]);
                            }}
                            renderInput={(params) => (
                                <TextField {...params} label="Select Property" variant="outlined" fullWidth />
                            )}
                            sx={{ marginBottom: '15px' }}
                        />
                    </div>
                    <div className="w-full md:w-[300px] mb-[20px] md:mb-0">
                        <Autocomplete
                            options={unitOptions}
                            value={selectedUnits}
                            onChange={(e, val) => setSelectedUnits(val)}
                            multiple
                            renderInput={(params) => (
                                <TextField {...params} label="Select Unit" variant="outlined" fullWidth />
                            )}
                        />
                    </div>
                </div>
                <div className="flex flex-col md:flex-row gap-[15px] w-full">
                    <MoneyCard
                        label="Remaining Balance"
                        info={
                            <>
                                PHP<br />
                                {formatMoney(balance)}
                            </>
                        }
                    />
                    <MoneyCard
                        label="Invoice Amount"
                        info={
                            <>
                                PHP<br />
                                {formatMoney(invoiceAmount)}
                            </>
                        }
                    />
                    <MoneyCard
                        label="Invoice Past Due"
                        info={
                            <>
                                PHP<br />
                                {formatMoney(pastDue)}
                            </>
                        }
                    />
                </div>
            </div>
            <div className="bt-button-container grid grid-cols-2 gap-2 md:flex md:flex-row md:gap-[10px]">
                <div className="bt-button flex flex-col h-[80px] md:flex-row md:w-[130px] md:h-[60px]">
                    <ChargeInvoiceForm
                        onNewInvoice={() => {
                            queryClient.invalidateQueries(['v2-invoices']);
                        }}
                    />
                </div>
                <div className="bt-button flex flex-col h-[80px] md:flex-row md:w-[130px] md:h-[60px]">
                    <LoadBalanceForm selectedAccount={selectedAccount} currentBalance={balance} />
                </div>
                <div className="bt-button flex flex-col h-[80px] md:flex-row md:w-[130px] md:h-[60px]" style={{ backgroundColor: '#BBBBBB' }}>
                    <i class="fas fa-credit-card mr-0 md:mr-[15px]"></i>
                    <p className="text-center md:text-left mt-[10px] md:mt-0">Issue<span className="hidden md:inline"><br /></span> Credit</p>
                </div>
                <div className="bt-button flex flex-col h-[80px] md:flex-row md:w-[130px] md:h-[60px]" style={{ backgroundColor: '#BBBBBB' }}>
                    <i class="fas fa-circle-dollar-to-slot mr-0 md:mr-[15px]"></i>
                    <p className="text-center md:text-left mt-[10px] md:mt-0">Give<span className="hidden md:inline"><br /></span> Refund</p>
                </div>
                <div className="bt-button col-span-2 flex flex-col h-[80px] md:flex-row md:w-[130px] md:h-[60px]">
                    <Tooltip
                        title={
                            !selectedProperty || selectedUnits?.length !== 1
                                ? 'Select a property and one unit to create'
                                : ''
                        }
                        arrow
                        disableInteractive
                    >
                        <span>
                            <div className="bt-button flex flex-col h-[80px] md:flex-row md:w-[130px] md:h-[60px]" onClick={() => setSOAOverlayOpen(true)}>
                                <i className="fas fa-file-alt mr-0 md:mr-[15px]"></i>
                                <p className="text-center md:text-left mt-[10px] md:mt-0">Create<span className="hidden md:inline"><br /></span> SOA</p>
                            </div>
                        </span>
                    </Tooltip>
                </div>
            </div>

            {/* Desktop View */}
            <div className="page-table-container hidden md:block">
                <table className="page-table">
                    <colgroup>
                        <col style={{ width: '8%' }} />
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '6%' }} />
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '14%' }} />
                        <col style={{ width: '16%' }} />
                        <col style={{ width: '9%' }} />
                        <col style={{ width: '11%' }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Type</th>
                            <th>Reference No.</th>
                            <th>C/D</th>
                            <th>Date Created</th>
                            <th>Date Due</th>
                            <th>Amount</th>
                            <th>Allocated Amount</th>
                            <th></th>
                            <th>Open Balance</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredRows === null ? (
                            <tr><td colSpan="9" style={{ textAlign: 'center' }}>No property selected</td></tr>
                        ) : filteredRows.length > 0 ? (
                            filteredRows.map(row => (
                                <tr
                                    key={row.id}
                                    className={selectedRow?.id === row.id ? 'selected' : ''}
                                    onClick={() => handleRowClick(row)}
                                >
                                    <td>{row.type}</td>
                                    <td>{row.reference}</td>
                                    <td>{row.cd}</td>
                                    <td>{row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '-'}</td>
                                    <td>
                                        {row.dueDate && !isNaN(new Date(row.dueDate).getTime())
                                            ? new Date(row.dueDate).toLocaleDateString()
                                            : '-'}
                                    </td>
                                    <td>PHP {formatMoney(row.amount)}</td>
                                    <td>
                                        {row.isInvoice ? (
                                            <>
                                                PHP {formatMoney(row.allocatedAmount)}{" "}
                                                <span style={{ color: row.openBalance === 0 ? "green" : "red" }}>
                                                    ({row.openBalance === 0 ? "Closed" : "Open"})
                                                </span>
                                            </>
                                        ) : '-'}
                                    </td>
                                    <td>
                                        {row.isInvoice && row.openBalance !== 0 ? (
                                            <div className="table-button-container">
                                                <button
                                                    className="action-buttons"
                                                    onClick={(e) => { e.stopPropagation(); viewEditAllocation(row); }}
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                                {row.reference === "-" ? (
                                                    <Tooltip title="No payment allocation to view" placement="top" arrow disableInteractive>
                                                        <span>
                                                            <button className="action-buttons disabled">
                                                                <i className="fas fa-eye"></i>
                                                            </button>
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    <button
                                                        className="action-buttons"
                                                        onClick={(e) => { e.stopPropagation(); handleViewAllocation(row); }}
                                                    >
                                                        <i className="fas fa-eye"></i>
                                                    </button>
                                                )}
                                            </div>
                                        ) : <div></div>}
                                    </td>
                                    <td>{row.isInvoice ? `PHP ${formatMoney(row.openBalance)}` : '-'}</td>
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan="9" style={{ textAlign: 'center' }}>No data found</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Mobile View */}
            <div className="md:hidden border-t-[1.5px] border-[#E8E8E8] mt-[20px]" />
            <div className="md:hidden flex space-between gap-[10px] mt-[20px]">
                <button
                    className={`tab-buttons ${activeTab === 'invoices' ? 'active' : ''}`}
                    onClick={() => setActiveTab('invoices')}
                    style={{ width: '100%' }}
                >
                    Invoices
                </button>
                <button
                    className={`tab-buttons ${activeTab === 'payments' ? 'active' : ''}`}
                    onClick={() => setActiveTab('payments')}
                    style={{ width: '100%' }}
                >
                    Payments
                </button>
            </div>
            <BalanceTransactionCards
                combinedRows={combinedRows}
                activeTab={activeTab}
                selectedRow={selectedRow}
                handleRowClick={handleRowClick}
                viewEditAllocation={viewEditAllocation}
                handleViewAllocation={handleViewAllocation}
            />

            {soaOverlayOpen && selectedProperty && selectedUnits?.length === 1 && (
                <div className="popup-overlay">
                    <div className="popup-content">
                        <button className="close-popup" onClick={() => setSOAOverlayOpen(false)}>
                            <i className="fas fa-times"></i>
                        </button>
                        <h2 className="text-2xl text-black">Create SOA</h2>
                        <div className="space-section">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Start Date"
                                    value={startDate}
                                    onChange={(newValue) => setStartDate(newValue)}
                                    sx={{ width: "100%", marginBottom: "1rem" }}
                                />
                                <DatePicker
                                    label="End Date"
                                    value={endDate}
                                    onChange={(newValue) => setEndDate(newValue)}
                                    sx={{ width: "100%", marginBottom: "1rem" }}
                                />
                            </LocalizationProvider>
                        </div>
                        <Tooltip
                            title={
                                !startDate || !endDate
                                    ? 'Input start and end date to create'
                                    : ''
                            }
                            arrow
                            disableInteractive
                        >
                            <button
                                className="button"
                                style={{ marginTop: '30px', marginBottom: '10px' }}
                                onClick={reactToPrintFn}
                            >
                                Create SOA
                            </button>
                        </Tooltip>
                        <div>
                            <GeneratedSOA
                                selectedAccount={selectedAccount.label}
                                selectedProperty={selectedProperty.label}
                                selectedUnits={selectedUnits[0].label}
                                startDate={startDate}
                                endDate={endDate}
                                transactions={combinedRows}
                                contentRef={contentRef}
                            />
                        </div>
                    </div>
                </div>
            )}

            {invoiceOverlayOpen && (
                <div className="popup-overlay">
                    <div className="popup-content">
                        <button className="close-popup" onClick={() => {
                            setInvoiceOverlayOpen(false);
                            setSelectedRow(null);
                        }}>
                            <i className="fas fa-times"></i>
                        </button>
                        <h2 className="text-2xl">Invoice Details</h2>
                        <table className="form-table">
                            <tbody>
                                <tr>
                                    <td style={{ width: "300px" }}>
                                        <div className="detail-column"
                                        >
                                            <p className="detail-label">Property</p>
                                            <p className="detail">{selectedRow.property || '-'}</p>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="detail-column">
                                            <p className="detail-label">Unit</p>
                                            <p className="detail">{selectedRow.unit || '-'}</p>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="w-[180px] md:w-[300px]">
                                        <div className="detail-column">
                                            <p className="detail-label">Charged To</p>
                                            <p className="detail">{selectedAccount.label}</p>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="detail-column">
                                            <p className="detail-label">Issued By</p>
                                            <p className="detail">{selectedRow.issuedBy ?? '-'}</p>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="w-[180px] md:w-[300px]">
                                        <div className="detail-column"
                                        >
                                            <p className="detail-label">Vendor</p>
                                            <p className="detail">{selectedRow.vendor ?? '-'}</p>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="detail-column">
                                            <p className="detail-label">Is Recurring</p>
                                            <p className="detail">{selectedRow.isRecurring ?? '-'}</p>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="w-[180px] md:w-[300px]">
                                        <div className="detail-column"
                                        >
                                            <p className="detail-label">Due Date</p>
                                            <p className="detail">{selectedRow.dueDate ? new Date(selectedRow.dueDate).toLocaleDateString() : '-'}</p>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="detail-column">
                                            <p className="detail-label">Status</p>
                                            <p className="detail">
                                                <span className={`accounting-status-badge ${selectedRow.status?.toLowerCase()}`}>
                                                    {selectedRow.status
                                                        ? selectedRow.status.charAt(0).toUpperCase() + selectedRow.status.slice(1)
                                                        : ''}
                                                </span>
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <h3 style={{ marginBottom: '5px' }}>Invoice Items</h3>
                        <p className="paid-amount mb-4"><span>Total Amount:</span> PHP {selectedRow.amount}</p>
                        <table className="invoice-items-table">
                            <thead>
                                <tr>
                                    <th>Invoice Category</th>
                                    <th>Description</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedRow.invoiceItems?.map((item) => (
                                    <tr key={item._id}>
                                        <td>{item.itemName?.trim() ? item.itemName : '-'}</td>
                                        <td>{item.description}</td>
                                        <td>PHP {formatMoney(item.amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {paymentOverlayOpen && (
                <div className="popup-overlay">
                    <div className="popup-content">
                        <button className="close-popup" onClick={() => {
                            setPaymentOverlayOpen(false);
                            setSelectedRow(null);
                        }}>
                            <i className="fas fa-times"></i>
                        </button>
                        <h2 className="text-2xl mb-[30px]">Payment Details</h2>
                        <div className="detail-column">
                            <p className="detail-label">Reference Number</p>
                            <p className="detail">{selectedRow.reference}</p>
                        </div>
                        <table className="form-table">
                            <tbody>
                                <tr>
                                    <td className="w-[180px] md:w-[300px]">
                                        <div className="detail-column">
                                            <p className="detail-label">Account</p>
                                            <p className="detail">{selectedAccount.label}</p>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="detail-column">
                                            <p className="detail-label">Payment Date</p>
                                            <p className="detail">{selectedRow.paymentDate ? new Date(selectedRow.paymentDate).toLocaleDateString() : '-'}</p>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <h3 style={{ marginTop: "10px" }}>Payment</h3>
                        <table className="form-table">
                            <tbody>
                                <tr>
                                    <td className="w-[180px] md:w-[300px]">
                                        <div className="detail-column">
                                            <p className="detail-label">Amount</p>
                                            <p className="detail">PHP {formatMoney(selectedRow.amount)}</p>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="detail-column">
                                            <p className="detail-label">Payment Method</p>
                                            <p className="detail">{selectedRow.paymentMethod ? selectedRow.paymentMethod : '-'}</p>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <div className="detail-column">
                                            <p className="detail-label">Card Number</p>
                                            <p className="detail">
                                                {(selectedRow.paymentMethod === 'Card' || selectedRow.paymentMethod === 'Credit Card') && selectedRow.cardNumber
                                                    ? `**** **** **** ${selectedRow.cardNumber}`
                                                    : '-'}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {viewAllocOverlayOpen && (
                <div className="popup-overlay">
                    <div className="popup-content">
                        <button className="close-popup" onClick={() => {
                            setViewAllocOverlayOpen(false);
                            setSelectedRow(null);
                        }}>
                            <i className="fas fa-times"></i>
                        </button>
                        <h2 className="text-2xl mb-[30px]">Payment Details</h2>
                        <div className="detail-column"
                        >
                            <p className="detail-label">Refererence Number</p>
                            <p className="detail">{selectedRow.reference}</p>
                        </div>
                        <table className="form-table">
                            <tbody>
                                <tr>
                                    <td className="w-[180px] md:w-[300px]">
                                        <div className="detail-column">
                                            <p className="detail-label">Payment Date</p>
                                            <p className="detail">{selectedRow.paymentDate ? new Date(selectedRow.paymentDate).toLocaleDateString() : '-'}</p>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="detail-column">
                                            <p className="detail-label">Status</p>
                                            <p className="detail" style={{ color: selectedRow.openBalance === 0 ? "green" : "red" }}>{selectedRow.openBalance === 0 ? "Closed" : "Open"}</p>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <h3 style={{ marginBottom: '5px' }}>Allocation</h3>
                        <p className="paid-amount" style={{ marginTop: "10px" }}><span>Total Paid:</span> PHP {selectedRow.allocatedAmount}</p>
                        <p className="paid-amount" style={{ marginTop: "5px", marginBottom: "20px" }}><span>Open Balance:</span> PHP {formatMoney(selectedRow.openBalance)}</p>
                        <table className="invoice-items-table">
                            <thead>
                                <tr>
                                    <th>Invoice Category</th>
                                    <th>Description</th>
                                    <th>Allocated Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedRow.invoiceItems?.map((item) => {
                                    return (
                                        <tr key={item._id}>
                                            <td>{item.itemName?.trim() ? item.itemName : '-'}</td>
                                            <td>{item.description}</td>
                                            <td>PHP {formatMoney(item.amount - item.balance)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {editAllocOverlayOpen && (
                <div className="popup-overlay">
                    <div className="popup-content">
                        <button className="close-popup" onClick={() => {
                            setEditAllocOverlayOpen(false);
                            setSelectedRow(null);
                        }}>
                            <i className="fas fa-times"></i>
                        </button>
                        <h2 className="text-2xl mb-[30px]">Allocate Amount</h2>
                        <h3 style={{ marginBottom: "25px" }}>Invoice Details</h3>
                        <div className="detail-column">
                            <p className="detail-label">Reference No.</p>
                            <p className="detail">{selectedRow.reference}</p>
                        </div>
                        <table className="form-table">
                            <tbody>
                                <tr>
                                    <td className="w-[180px] md:w-[300px]">
                                        <div className="detail-column">
                                            <p className="detail-label">Issued By</p>
                                            <p className="detail">{selectedRow.issuedBy ?? '-'}</p>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="detail-column">
                                            <p className="detail-label">Due Date</p>
                                            <p className="detail">{selectedRow.dueDate ? new Date(selectedRow.dueDate).toLocaleDateString() : '-'}</p>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <p className="paid-amount mb-4"><span>Vendor:</span>{selectedRow.vendor ?? '-'}</p>
                        <table className="invoice-items-table">
                            <thead>
                                <tr>
                                    <th className="hidden md:table-cell">Invoice Category</th>
                                    <th>Description</th>
                                    <th>Open Balance</th>
                                    <th>Allocated</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedRow.invoiceItems?.map((item) => {
                                    return (
                                        <tr key={item._id}>
                                            <td className="hidden md:table-cell">
                                                {item.itemName?.trim() ? item.itemName : '-'}
                                            </td>
                                            <td>{item.description}</td>
                                            <td>
                                                {selectedRow.reference === "-" ? item.amount : item.balance}
                                            </td>
                                            <td>
                                                <TextField
                                                    id={`allocation-${item._id}`}
                                                    size="small"
                                                    variant="standard"
                                                    style={{ width: '55px' }}
                                                    sx={{ '& .MuiInputBase-input': { fontSize: '14px' } }}
                                                    value={paymentAllocations[item._id]}
                                                    onChange={(e) => handleAllocationChange(e, item._id)}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {selectedRow.reference !== "-" && (
                            <p className="checkpoint">Last allocated in {new Date(selectedRow.updatedAt).toLocaleString()}</p>
                        )}
                        <button className="button" onClick={(e) => handleEditAllocation(e, selectedRow)}>Confirm</button>
                    </div>
                </div>
            )}
        </div>
    );
};

const fetchProperties = async () => {
    const response = await axios.get(`${config.API_URL}/v2/property`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
    return response.data.map(p => ({ value: p._id, label: p.name }));
};

const fetchRoles = async () => {
    const response = await axios.get(`${config.API_URL}/v2/role/unitRole`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
    return response.data.filter(r => r.propertyRole.role === 'owner' || r.propertyRole.role === 'tenant');

    // v2/role/unitRole/query?limit=0&role=owner,tenant
    // return response.data.results;

    // v2/role/unitRole
    // return response.data.filter(r => r.propertyRole.role === 'owner' || r.propertyRole.role === 'tenant');
};

const fetchBalances = async () => {
    const response = await axios.get(`${config.API_URL}/v2/accountBalance/balance`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
    return response.data.balances;
};

const fetchTransactions = async () => {
    const response = await axios.get(`${config.API_URL}/v2/accountTransaction/credit`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
    return response.data.transactions;
};

const fetchInvoices = async () => {
    const response = await axios.get(`${config.API_URL}/v2/invoice`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
    return response.data;
};

function formatMoney(amount) {
    const num = Number(amount) || 0;
    return num.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

export default MBalanceTransactions;